/**
 * GameScene — the main play scene.
 *
 * Layout (landscape, 960x540 logical canvas, FIT-scaled to the device):
 *   - Left half:  drag-joystick (touch) / WASD or arrow keys (keyboard) for movement.
 *   - Right half: two big circular touch buttons — CHOMP and BEAM.
 *   - FIRE is automatic — the newt shoots constantly while alive.
 *   - Top HUD: 3 lives (white squares), 20/20 chomp counter, score.
 *   - Center: charge gauge for the godzilla beam.
 *   - Boss arena: single boss sprite at the end of level 4.
 *
 * Godzilla transformation (per user spec, 2026-08-04):
 *   - After 20 chomped enemies (the player walks into an enemy and the melee
 *     hits), the newt transforms into the godzilla placeholder.
 *   - The beam widens, deals more damage.
 *   - Boss kill grants +1 life (starting from 3, growing per boss).
 *   - Picking up an upgrade pickup restores 1 life and removes any
 *     invulnerability window.
 *
 * Chomp also heals (per user spec): every time the player successfully
 * chomps at least one enemy, they recover one tick of lost HP. The
 * implementation also tightens to the "risking getting hit" intent — the
 * chomp bypasses the invulnerability window so using chomp while enemies
 * are around is a deliberate risk-vs-reward.
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../Game";
import { Player } from "../entities/Player";
import { Enemy, type EnemyKind } from "../entities/Enemy";
import { Boss } from "../entities/Boss";
import { Projectile } from "../entities/Projectile";
import { UpgradePickup } from "../entities/UpgradePickup";
import {
  UPGRADE_LABELS,
  type UpgradeId,
} from "../systems/upgradeIds";
import { UpgradeSystem } from "../systems/UpgradeSystem";
import { InputManager } from "../systems/InputManager";
import { WaveManager } from "../systems/WaveManager";
import { BackgroundSystem } from "../systems/BackgroundSystem";
import { toggleFullscreen } from "../systems/fullscreen";
import { TEX } from "../textures";
import { showGameOver, showPauseMenu, showCongratsPanel, submitRunScore, clearPlayerIdent, showInfoPrompt } from "../../ui/overlay";
import { Settings } from "../../settings";
import { ACTIVE_DIFFICULTY } from "../config/difficultyConfig";

/** Initial lives. Boss-kill grants +1 (so the player grows as the run continues). */
/** Chomps needed to trigger the godzilla transformation. */
// Configured via ACTIVE_DIFFICULTY.chompsForGodzilla and ACTIVE_DIFFICULTY.maxHpCap

export class GameScene extends Phaser.Scene {
  // ---- entities -------------------------------------------------------
  private player!: Player;
  private enemies!: Phaser.Physics.Arcade.Group;
  private enemyBullets!: Phaser.Physics.Arcade.Group;
  private bullets!: Phaser.Physics.Arcade.Group;
  private chomps!: Phaser.Physics.Arcade.Group;
  private pickups!: Phaser.Physics.Arcade.Group;
  private boss: Boss | null = null;
  private bossGroup!: Phaser.Physics.Arcade.Group;

  // ---- wave state ----------------------------------------------------
  private waves!: WaveManager;
  private upgrades!: UpgradeSystem;
  private inputMgr!: InputManager;
  private bgSystem: BackgroundSystem | null = null;

  private lastEnemySpawnAt = 0;
  private killsSinceLastUpgrade = 0;
  private killsSinceLastHpIncrease = 0;
  private lastFireAt = 0;
  private lostHpCounter = 0;
  private recoveryDropQueue: UpgradeId[] = [];

  // ---- godzilla form -------------------------------------------------
  private chompKills = 0;
  private score = 0;
  private autoFire = true;
  private gameOver = false;
  private godzillaFiring = false;
  private beamEndTime = 0;
  private beamDamageTickAt = 0;

  // ---- touch HUD zones ------------------------------------------------
  private chompZone!: Phaser.GameObjects.Zone;
  private chargeZone!: Phaser.GameObjects.Zone;

  // ---- HUD text + graphics -------------------------------------------

  private currentBGM?: Phaser.Sound.BaseSound;
  private scoreText!: Phaser.GameObjects.Text;
  private lifeImages: Phaser.GameObjects.Image[] = [];
  private hpImages: Phaser.GameObjects.Image[] = [];
  private upgradeUI: Phaser.GameObjects.GameObject[] = [];
  private livesTextObj?: Phaser.GameObjects.Text;
  private hpTextObj?: Phaser.GameObjects.Text;
  private chargeBar!: Phaser.GameObjects.Graphics;
  private bossHealthBar!: Phaser.GameObjects.Graphics;
  private beamGraphics!: Phaser.GameObjects.Graphics;
  private chompBtnBase!: Phaser.GameObjects.Graphics;
  private chompBtnText!: Phaser.GameObjects.Text;
  private chompProgressGfx!: Phaser.GameObjects.Graphics;
  private chompBtnX!: number;
  private chompBtnY!: number;
  private chompBtnRadius!: number;
  private chargeBtnBase!: Phaser.GameObjects.Graphics;
  private chargeBtnText!: Phaser.GameObjects.Text;
  private chompCountText!: Phaser.GameObjects.Text;
  private godzillaText!: Phaser.GameObjects.Text;
  private chompFlickerTween?: Phaser.Tweens.Tween;
  private chargeSoundInstance?: Phaser.Sound.BaseSound;
  private chargeFull = false;
  private chargeTimeoutAt = 0;

  constructor() {
    super({ key: "GameScene" });
  }

  /** Safe wrapper — only plays if the audio key was successfully loaded. */
  private sfx(key: string, config?: Phaser.Types.Sound.SoundConfig): void {
    if (!Settings.sfx) return;
    if (this.cache.audio.exists(key)) {
      this.sound.play(key, config);
    }
  }

  private playBgm(key: string): void {
    if (this.currentBGM && this.currentBGM.key === key) {
      if (!Settings.bgm) this.currentBGM.pause();
      else if (this.currentBGM.isPaused) this.currentBGM.resume();
      return;
    }
    if (this.currentBGM) {
      this.currentBGM.stop();
      this.currentBGM = undefined;
    }
    if (this.cache.audio.exists(key)) {
      this.currentBGM = this.sound.add(key, { loop: true, volume: Settings.bgm ? 0.45 : 0 });
      this.currentBGM.play();
    }
  }

  private updateBgmVolume = (): void => {
    if (this.currentBGM) {
      (this.currentBGM as Phaser.Sound.WebAudioSound).setVolume(Settings.bgm ? 0.45 : 0);
      if (Settings.bgm && this.currentBGM.isPaused) this.currentBGM.resume();
      else if (!Settings.bgm && !this.currentBGM.isPaused) this.currentBGM.pause();
    }
  };

  public create(): void {
    this.score = 0;
    this.gameOver = false;
    this.autoFire = true;
    this.chompKills = 0;
    this.boss = null;
    this.killsSinceLastUpgrade = 0;
    this.lastFireAt = 0;
    this.lastEnemySpawnAt = 0;
    this.godzillaFiring = false;
    this.beamEndTime = 0;
    this.beamDamageTickAt = 0;
    this.chargeFull = false;
    this.chargeTimeoutAt = 0;
    this.lostHpCounter = 0;
    this.recoveryDropQueue = [];
    this.killsSinceLastHpIncrease = 0;

    this.cameras.main.setBackgroundColor("#000000");
    this.upgrades = new UpgradeSystem();

    // ---- Player (Space Newt) ----------------------------------------
    // The player ship is a big white rectangle (320x190 source → 144x86
    // visible at 0.45×), positioned at the bottom-left third of the
    // canvas. Big enough to be unmistakable on any phone.
    this.player = new Player(this, GAME_WIDTH * 0.14, GAME_HEIGHT * 0.65);
    this.player.setOrigin(0.5, 0.5);

    // ---- Physics groups -----------------------------------------------
    // Force the classType + frame overrides so Phaser actually instantiates
    // the right class. Empty-groups run with `maxSize=10` by default.
    this.enemies = this.physics.add.group({
      classType: Enemy,
      maxSize: 30,
      runChildUpdate: false,
    });
    this.enemyBullets = this.physics.add.group({
      classType: Projectile,
      maxSize: 60,
      runChildUpdate: false,
    });
    this.bullets = this.physics.add.group({
      classType: Projectile,
      maxSize: 100,
      runChildUpdate: false,
    });
    this.chomps = this.physics.add.group({
      classType: Projectile,
      maxSize: 8,
      runChildUpdate: false,
    });
    this.pickups = this.physics.add.group({
      classType: UpgradePickup,
      maxSize: 4,
      runChildUpdate: true,
    });

    this.bossGroup = this.physics.add.group({
      classType: Boss,
      runChildUpdate: false,
    });

    // ---- Wave manager ------------------------------------------------
    this.waves = new WaveManager(this);

    // ---- Input -------------------------------------------------------
    this.inputMgr = new InputManager(this);

    // ---- Colliders ---------------------------------------------------
    this.physics.add.overlap(this.bullets, this.enemies, (objA, objB) =>
      this.onBulletHitsEnemy(objA as Projectile, objB as Enemy)
    );
    // Boss overlap — registered ONCE against the persistent bossGroup,
    // not per-spawn. Accumulating overlaps was a key freeze cause.
    this.physics.add.overlap(this.bullets, this.bossGroup, (objA, objB) =>
      this.onBulletHitsBoss(objA as Projectile, objB as Boss)
    );
    this.physics.add.overlap(this.player, this.enemies, () => this.onPlayerHitsEnemy());
    this.physics.add.overlap(this.player, this.enemyBullets, () => this.onPlayerHitsBullet());
    this.physics.add.overlap(this.player, this.pickups, (_a, b) =>
      this.onPlayerPicksUpUpgrade(b as UpgradePickup)
    );

    // ---- Background (procedural starfield) ---------------------------
    this.bgSystem = new BackgroundSystem(this);
    void this.bgSystem.build();

    // ---- HUD --------------------------------------------------------
    // Trashstock logo next to score

    this.scoreText = this.add.text(GAME_WIDTH / 2, 8, "SCORE 0", this.hudStyle(18)).setOrigin(0.5, 0);
    this.scoreText.setName("scoreText");
    this.scoreText.setDepth(100);
    this.drawLivesIcons();

    // CHOMPES counter — top-center, prominent.
    this.chompCountText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 24,
      `LASER POWER 0/${ACTIVE_DIFFICULTY.chompsForGodzilla}`,
      this.hudStyle(20)
    ).setOrigin(0.5, 1);
    this.chompCountText.setName("chompCountText");
    this.chompCountText.setDepth(100);

    // GODZILLA MODE banner — hidden until transform.
    this.godzillaText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, "GODZILLA MODE — BEAM READY", this.hudStyle(22)).setOrigin(0.5);
    this.godzillaText.setVisible(false);
    this.godzillaText.setDepth(100);

    this.chargeBar = this.add.graphics();
    this.chargeBar.setVisible(false);
    this.chargeBar.setDepth(100);

    this.bossHealthBar = this.add.graphics();
    this.bossHealthBar.setVisible(false);
    this.bossHealthBar.setDepth(100);

    this.beamGraphics = this.add.graphics();
    this.beamGraphics.setDepth(15);

    // ---- Touch buttons (right side, big circles) ---------------------
    // Layout: two 100-px-diameter circles stacked on the right edge.
    // CHOMP on top, BEAM below it. The newt auto-fires — no FIRE button.
    this.createBigButtons();

    // ---- Mobile bottom-row: PAUSE -------------------
    const pauseBtn = this.add
      .text(20, GAME_HEIGHT - 4, "[ PAUSE ]", this.hudStyle(16))
      .setOrigin(0, 1)
      .setInteractive(new Phaser.Geom.Rectangle(-20, -20, 120, 50), Phaser.Geom.Rectangle.Contains)
      .on("pointerdown", () => {
        this.scene.pause();
        void showPauseMenu(document.getElementById("overlay-root"));
      });
    pauseBtn.setName("pauseBtn");
    pauseBtn.setDepth(100);
    this.input.keyboard?.addKey("ESC").on("down", () => { this.scene.pause(); void showPauseMenu(document.getElementById("overlay-root")); });
    this.input.keyboard?.addKey("SPACE").on("down", () => { this.scene.pause(); void showPauseMenu(document.getElementById("overlay-root")); });
    this.input.keyboard?.addKey("R").on("down", () => this.restart());

    this.lostHpCounter = 0;
    this.shootingEnemiesSpawnedThisWave = 0;
    this.grantRandomStarterUpgrade();

    this.flashLevel(this.waves.currentPhase().name);
    this.refreshHud();
    
    const resumeHandler = () => {
      this.scene.resume();
    };
    const restartHandler = () => {
      this.scene.resume();
      this.restart();
    };
    window.addEventListener("traszka:resume", resumeHandler);
    window.addEventListener("traszka:restart", restartHandler);
    window.addEventListener("traszka:settings-changed", this.updateBgmVolume);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener("traszka:resume", resumeHandler);
      window.removeEventListener("traszka:restart", restartHandler);
      window.removeEventListener("traszka:settings-changed", this.updateBgmVolume);
    });

    // Resume normal BGM
    this.playBgm("bgm");

    // Fullscreen toggle button
    this.add.text(GAME_WIDTH - 20, 20, "[ FULLSCREEN ]", this.hudStyle(18))
      .setOrigin(1, 0)
      .setInteractive(new Phaser.Geom.Rectangle(-40, -40, 180, 100), Phaser.Geom.Rectangle.Contains)
      .setDepth(100)
      .on("pointerdown", () => {
        toggleFullscreen(this.scale);
      });

    // Events
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener("traszka:resume", resumeHandler);
    });
  }

  /**
   * Draw squares for lives, and smaller squares for HP.
   */
  private drawLivesIcons(): void {
    if (!this.player) return;

    for (const img of this.lifeImages) img.destroy();
    for (const img of this.hpImages) img.destroy();
    for (const obj of this.upgradeUI) obj.destroy();
    this.lifeImages = [];
    this.hpImages = [];
    this.upgradeUI = [];
    if (this.livesTextObj) this.livesTextObj.destroy();
    if (this.hpTextObj) this.hpTextObj.destroy();

    const sq = 24;
    const hpSq = 20;
    const gap = 6;
    const margin = 10;
    let y = 10;
    
    this.livesTextObj = this.add.text(margin, y + sq/2, "LIVES", this.hudStyle(12)).setOrigin(0, 0.5).setDepth(100);
    const lifeX = margin + 85;
    
    for (let i = 0; i < this.player.lives; i++) {
      const img = this.add.image(lifeX + i * (sq + gap), y, "trashstock_logo");
      img.setOrigin(0, 0);
      img.setDisplaySize(sq, sq);
      img.setDepth(100);
      this.lifeImages.push(img);
    }
    y += sq + gap;
    
    this.hpTextObj = this.add.text(margin, y + hpSq/2, "SHIELD", this.hudStyle(12)).setOrigin(0, 0.5).setDepth(100);
    const hpX = margin + 95;
    for (let i = 0; i < this.player.maxHp; i++) {
      const img = this.add.image(hpX + i * (hpSq + gap), y, TEX.HITPOINT);
      img.setOrigin(0, 0);
      img.setDisplaySize(hpSq, hpSq);
      img.setDepth(100);
      if (i >= this.player.hp) {
         img.setAlpha(0.3); // faded for lost hp
      }
      this.hpImages.push(img);
    }
    y += hpSq + gap;

    // Upgrades UI
    const upgSq = 12;
    const uGap = 4;
    const upgLabels = [
      { key: "MULTISHOT", label: "MSHOT", val: this.upgrades.state.multishot, base: 1, max: ACTIVE_DIFFICULTY.upgrades.MULTISHOT.maxLevel },
      { key: "SPREAD", label: "SPREAD", val: this.upgrades.state.spread, base: 0, max: ACTIVE_DIFFICULTY.upgrades.SPREAD.maxLevel },
      { key: "FIRE_RATE", label: "SPEED", val: this.upgrades.state.fireRate, base: 0, max: ACTIVE_DIFFICULTY.upgrades.FIRE_RATE.maxLevel },
      { key: "PIERCING", label: "PIERCE", val: this.upgrades.state.piercing, base: 0, max: ACTIVE_DIFFICULTY.upgrades.PIERCING.maxLevel },
      { key: "SPEED", label: "MOVE", val: this.upgrades.state.speed, base: 0, max: ACTIVE_DIFFICULTY.upgrades.SPEED.maxLevel },
      { key: "SHOT_POWER", label: "POWER", val: this.upgrades.state.shotPower, base: 0, max: ACTIVE_DIFFICULTY.upgrades.SHOT_POWER.maxLevel },
    ];

    for (const upg of upgLabels) {
      const txt = this.add.text(margin, y + upgSq/2, upg.label, this.hudStyle(10)).setOrigin(0, 0.5).setDepth(100);
      this.upgradeUI.push(txt);
      
      const dotsX = margin + 65;
      const totalDots = upg.max - upg.base;
      const earned = upg.val - upg.base;
      for (let i = 0; i < totalDots; i++) {
        const dot = this.add.image(dotsX + i * (upgSq + uGap), y, TEX.HITPOINT);
        dot.setOrigin(0, 0);
        dot.setDisplaySize(upgSq, upgSq);
        dot.setDepth(100);
        if (i >= earned) {
          dot.setAlpha(0.2); // grayed out for max level possible
        }
        this.upgradeUI.push(dot);
      }
      y += upgSq + gap;
    }
  }

  // ---- HUD: big circular touch buttons -------------------------------

  /**
   * Build the right-side touch buttons: two big circles. CHOMP on top,
   * BEAM below it. No FIRE — the newt auto-fires.
   */
  private createBigButtons(): void {
    const dia = 110;
    const radius = dia / 2;
    const margin = 14;
    const rightX = GAME_WIDTH - radius - margin;
    const bottomY = GAME_HEIGHT - radius - margin;

    // BEAM is at the bottom right.
    const beamX = rightX;
    const beamY = bottomY;
    // CHOMP is above BEAM.
    const chompX = rightX;
    const chompY = beamY - dia - margin;
    // AUTO is left of BEAM.
    const autoX = beamX - dia - margin;
    const autoY = bottomY;

    this.chompZone = this.add.zone(chompX, chompY, dia, dia);
    this.chargeZone = this.add.zone(beamX, beamY, dia, dia);

    this.inputMgr.setActionZones(null, this.chompZone, this.chargeZone);

    const setZoneCircle = (z: Phaser.GameObjects.Zone): void => {
      (z as unknown as { setCircleDropZone: (r: number) => void }).setCircleDropZone(radius);
    };
    setZoneCircle(this.chompZone);
    setZoneCircle(this.chargeZone);

    // Draw the visible buttons - black fill with thick white border.
    const drawCircle = (x: number, y: number, label: string): { bg: Phaser.GameObjects.Graphics, text: Phaser.GameObjects.Text } => {
      const g = this.add.graphics();
      g.setDepth(99);
      g.fillStyle(0x000000, 1);
      g.fillCircle(x, y, radius);
      g.lineStyle(4, 0xffffff, 1);
      g.strokeCircle(x, y, radius - 2);
      const text = this.add.text(x, y, label, this.btnStyle(18)).setOrigin(0.5).setDepth(100);
      return { bg: g, text };
    };

    const chompObj = drawCircle(chompX, chompY, "CHOMP");
    this.chompBtnBase = chompObj.bg;
    this.chompBtnText = chompObj.text;
    this.chompBtnX = chompX;
    this.chompBtnY = chompY;
    this.chompBtnRadius = radius;
    this.chompProgressGfx = this.add.graphics().setDepth(101);

    const chargeObj = drawCircle(beamX, beamY, "CHARGE\nLASER");
    this.chargeBtnBase = chargeObj.bg;
    this.chargeBtnText = chargeObj.text;
    this.chargeBtnText.setFontSize("14px");
    this.chargeBtnText.setAlign("center");
    this.chargeBtnText.setLineSpacing(2);
    
    // Auto fire has line breaks so we use a specific style
    const autoBtnText = drawCircle(autoX, autoY, "AUTO\nFIRE").text;
    autoBtnText.setFontSize("14px"); // shrink slightly to fit circle
    autoBtnText.setAlign("center");
    autoBtnText.setLineSpacing(2);

    const autoCirc = this.add.circle(autoX, autoY, radius, 0x000000, 0).setInteractive();
    autoCirc.setDepth(100);
    autoCirc.on("pointerdown", () => {
      this.autoFire = !this.autoFire;
      autoBtnText.setColor(this.autoFire ? "#ffffff" : "#888888");
    });

    this.input.keyboard?.addKey("Z").on("down", () => {
      this.autoFire = !this.autoFire;
      autoBtnText.setColor(this.autoFire ? "#ffffff" : "#888888");
    });
  }

  private hudStyle(size: number): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: `${size}px`,
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 3,
    };
  }

  private btnStyle(size: number): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: `${size}px`,
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 4,
    };
  }

  private refreshHud(): void {
    this.scoreText.setText(`SCORE ${this.score}`);
    this.drawLivesIcons();
    this.chompCountText.setText(
      `LASER POWER ${Math.min(this.chompKills, ACTIVE_DIFFICULTY.chompsForGodzilla)}/${ACTIVE_DIFFICULTY.chompsForGodzilla}`
    );
    if (this.chompKills >= ACTIVE_DIFFICULTY.chompsForGodzilla && !this.chompFlickerTween) {
      this.chompFlickerTween = this.tweens.add({
        targets: this.chompCountText,
        alpha: 0.2,
        yoyo: true,
        repeat: -1,
        duration: 200
      });
    } else if (this.chompKills < ACTIVE_DIFFICULTY.chompsForGodzilla && this.chompFlickerTween) {
      this.chompFlickerTween.stop();
      this.chompFlickerTween = undefined;
      this.chompCountText.setAlpha(1);
    }
  }

  // ---- main update tick ---------------------------------------------

  public update(_time: number, deltaMs: number): void {
    if (this.gameOver) return;

    const now = this.time.now;
    const input = this.inputMgr.read();

    // Player movement
    this.player.moveWith(input.move, this.upgrades.movementSpeed());

    // Sync the visible rectangle (Graphics overlay) with the sprite each
    // frame so the ship is always drawn, regardless of texture state.
    this.player.refreshVisual();

    // Auto-fire (continuous, if enabled).
    // Cooldown is driven by the FIRE_RATE upgrade level; baseline 220 ms
    // and decreases as the player picks up FIRE_RATE pickups.
    const cooldown = this.upgrades.fireCooldownMs();
    if (this.autoFire && now - this.lastFireAt >= cooldown) {
      this.fireBulletBurst(now);
    }

    this.updateChompCooldownUI(now);
    this.updateChargeButtonUI(now);

    // Chomp - fires continuously while the button is held, gated by the
    // player's internal chomp cooldown so a held button still respects the
    // chomp's per-fire waiting period (e.g. 2500ms).
    // Chomp is DISABLED during godzilla laser firing.
    if (input.chompHeld && this.player.canChomp(now) && !this.godzillaFiring) {
      this.fireChomp(now);
    }

    // Toggle charge on press (no hold needed).
    // If we press while fully charged, fire the beam.
    if (input.chargePressed && this.chompKills > 0) {
      if (!this.player.charging && this.player.chargeLevel === 0) {
        this.player.startCharging(now);
        this.chargeBar.setVisible(true);
        if (this.chompKills >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
          this.activateGodzilla();
        }
        if (this.chargeSoundInstance) this.chargeSoundInstance.stop();
        this.chargeSoundInstance = this.sound.add("charge", { volume: 0.5 });
        this.chargeSoundInstance.play();
      } else if (this.player.charging && this.player.chargeLevel >= 1) {
        // Fully charged -> Fire!
        if (this.chargeSoundInstance) {
          this.chargeSoundInstance.stop();
          this.chargeSoundInstance = undefined;
        }
        this.releaseBeamIfReady();
      }
    }
    
    if (this.player.charging) {
      const lvl = this.player.updateCharge(now);
      this.drawChargeBar(lvl);
      if (lvl >= 1) {
        if (!this.chargeFull) {
          this.chargeFull = true;
          this.chargeTimeoutAt = now + 10000;
        }
        // Fully charged, waiting for player to fire
        if (this.chargeSoundInstance) {
          this.chargeSoundInstance.stop();
          this.chargeSoundInstance = undefined;
        }
      }
    }

    if (this.chargeFull && !this.godzillaFiring) {
      if (now > this.chargeTimeoutAt) {
        // Expiration penalty
        this.player.cancelCharge();
        this.chargeFull = false;
        this.chompKills = 0;
        this.chargeBar.setVisible(false);
        this.deactivateGodzilla();
        this.refreshHud();
      }
    }

    // Continuous godzilla beam tick
    if (this.godzillaFiring && now < this.beamEndTime) {
      this.updateContinuousBeam(now);
    } else if (this.godzillaFiring && now >= this.beamEndTime) {
      this.endContinuousBeam();
    }

    // Scroll the procedural background.
    const scrollSpeed = 80 + this.waves.difficulty() * 30;
    if (this.bgSystem) this.bgSystem.update(deltaMs, scrollSpeed);

    // Wave tick — if a level duration elapsed, spawn the boss.
    const elapsed = this.waves.tick(deltaMs / 1000);
    if (elapsed && !this.boss) {
      this.spawnBoss();
    }

    // Enemy / pickup spawns
    if (!this.boss) {
      this.maybeSpawnEnemy(now);
      this.bossHealthBar.setVisible(false);
    } else {
      this.boss.update(now);
      this.boss.updateEyes(this.player.x, this.player.y);
      this.bossShoot(now);
      this.bossHealthBar.setVisible(true);
      this.bossHealthBar.clear();
      const bw = 300;
      const bh = 10;
      const bx = 960 / 2 - bw / 2; // GAME_WIDTH is 960
      const by = 40;
      this.bossHealthBar.lineStyle(2, 0xffffff, 1);
      this.bossHealthBar.strokeRect(bx, by, bw, bh);
      this.bossHealthBar.fillStyle(0xffffff, 1); // Use white for Nokia style
      this.bossHealthBar.fillRect(bx, by, bw * (this.boss.hp / this.boss.maxHp), bh);
    }

    // Pickup drift animation + cull
    this.pickups.children.iterate((p) => {
      const pk = p as UpgradePickup;
      if (pk.active && pk.x < -40) {
        pk.collect();
      } else if (pk.active) {
        pk.update(now);
      }
      return true;
    });

    // Cull off-screen bullets, enemies, chomps
    this.bullets.children.iterate((b) => {
      const bullet = b as Projectile;
      if (bullet.active && bullet.x > GAME_WIDTH + 30) bullet.reset();
      return true;
    });
    this.enemies.children.iterate((e) => {
      const en = e as Enemy;
      if (en.active && en.x < -40) {
        en.disableBody(true, true);
      } else if (en.active && en.isOrbiting && this.boss && this.boss.active) {
        // Orbit around the boss
        en.orbitAngle += (deltaMs / 1000) * 1.8;
        const ox = this.boss.x + Math.cos(en.orbitAngle) * en.orbitRadius;
        const oy = this.boss.y + Math.sin(en.orbitAngle) * en.orbitRadius;
        en.setPosition(ox, oy);
        const body = en.body as Phaser.Physics.Arcade.Body;
        if (body) body.reset(ox, oy);

        if (en.isShooter) {
          en.setAlpha(0.5 + 0.5 * Math.sin(now / 60));
          if (now - en.lastShooterShotAt > 2800 && en.x > 30 && en.x < GAME_WIDTH - 10) {
            en.lastShooterShotAt = now;
            this.enemyShooterFire(en);
          }
        }
      } else if (en.active && en.isShooter) {
        // Special shooting enemies flicker continuously
        en.setAlpha(0.5 + 0.5 * Math.sin(now / 60));
        if (now - en.lastShooterShotAt > 2800 && en.x > 40 && en.x < GAME_WIDTH - 20) {
          en.lastShooterShotAt = now;
          this.enemyShooterFire(en);
        }
      }
      return true;
    });
    this.chomps.children.iterate((c) => {
      const ch = c as Projectile;
      if (ch.active && ch.x > GAME_WIDTH + 60) ch.reset();
      // chomps expire after 350ms — keep them short
      if (ch.active && now - (ch.getData("bornAt") ?? now) > 350) ch.reset();
      return true;
    });
    this.enemyBullets.children.iterate((b) => {
      const bullet = b as Projectile;
      if (bullet.active && bullet.x < -20) bullet.reset();
      return true;
    });
    void this.chargeBar;
  }

  /** Release the beam if the player held the key long enough. */
  private releaseBeamIfReady(): void {
    if (!this.player.charging) return;
    const now = this.time.now;
    const lvl = this.player.updateCharge(now);
    if (lvl >= 0.2 && this.chompKills > 0) {
      this.chargeFull = false;
      this.fireBeam(now);
    } else {
      this.player.charging = false;
      this.player.chargeLevel = 0;
      this.chargeFull = false;
      this.chargeBar.setVisible(false);
      this.deactivateGodzilla();
    }
  }

  // ---- spawners ----------------------------------------------------------

  private maybeSpawnEnemy(now: number): void {
    if (now - this.lastEnemySpawnAt < this.waves.spawnInterval() * 1000) return;
    this.lastEnemySpawnAt = now;

    const phase = this.waves.currentPhase();
    let kind = phase.enemyKind;

    // Mix enemies for Infinite Fusion
    if (phase.id >= 13) {
      const kindsToMix: EnemyKind[] = ["cardboard", "can", "plastic", "biowaste", "toxic", "radioactive", "chemicals"];
      kind = kindsToMix[Math.floor(Math.random() * kindsToMix.length)];
    }

    const y = Phaser.Math.Between(GAME_HEIGHT * 0.2, GAME_HEIGHT * 0.85);
    const x = GAME_WIDTH + 40;
    const enemy = this.enemies.get(x, y) as Enemy | null;
    if (!enemy) return;
    enemy.enableBody(true, x, y, true, true);
    enemy.setKind(kind);
    enemy.setActive(true);
    enemy.setVisible(true);
    enemy.hp = this.waves.enemyHp();
    enemy.maxHp = enemy.hp;
    enemy.kind = kind;
    enemy.setVelocity(-100 * this.waves.difficulty(), 0);
    enemy.setAlpha(1);
    enemy.setDepth(10);

    const currentWave = this.waves.current().wave;
    const startWave = ACTIVE_DIFFICULTY.shootingEnemiesStartWave;
    const maxShootersForWave = currentWave < startWave ? 0 : Math.min(
      ACTIVE_DIFFICULTY.shootingEnemiesMaxCount,
      ACTIVE_DIFFICULTY.shootingEnemiesBaseCount + (currentWave - startWave) * ACTIVE_DIFFICULTY.shootingEnemiesPerWave
    );
    if (currentWave >= startWave && this.shootingEnemiesSpawnedThisWave < maxShootersForWave) {
      const waveElapsed = this.waves.current().elapsed;
      const waveDuration = 25;
      const remainingTimeRatio = Math.max(0.1, (waveDuration - waveElapsed) / waveDuration);
      const remainingShootersNeeded = maxShootersForWave - this.shootingEnemiesSpawnedThisWave;
      const shooterChance = Math.min(0.8, remainingShootersNeeded / Math.max(1, 8 * remainingTimeRatio));

      if (Math.random() < shooterChance) {
        enemy.setShooter(true);
        this.shootingEnemiesSpawnedThisWave += 1;
      }
    }
  }

  private enemyShooterFire(enemy: Enemy): void {
    if (!this.player || !this.player.active) return;
    const b = this.enemyBullets.get(enemy.x - 10, enemy.y) as Projectile | null;
    if (!b) return;
    b.fireBullet(enemy.x - 10, enemy.y);
    b.setTexture(TEX.BOSS_BULLET);
    (b.body as Phaser.Physics.Arcade.Body).setCircle(8, 4, 4);
    b.setScale(1);
    b.damage = 1;

    const dx = this.player.x - enemy.x;
    const dy = this.player.y - enemy.y;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const speed = 220;
    b.setVelocity((dx / dist) * speed, (dy / dist) * speed);

    this.sfx("enemy_shoot", { volume: 0.4 });
  }

  // pickTexture was removed since setKind handles it directly now.

  private spawnBoss(): void {
    const phase = this.waves.currentPhase();
    this.boss = new Boss(this, GAME_WIDTH + 120, GAME_HEIGHT * 0.55, phase);
    // Progressively scale Boss HP using WaveManager which calculates based on PHASES and global difficulty
    this.boss.maxHp = this.waves.bossHp();
    this.boss.hp = this.boss.maxHp;
    this.boss.setDepth(12);
    // Add the boss to the persistent bossGroup so the overlap registered
    // once in create() covers it — no new overlap per spawn.
    this.bossGroup.add(this.boss);
    // Entrance screen shake
    this.cameras.main.shake(400, 0.012);
    // Animate the boss into the playfield by setting velocity over time
    // instead of mutating `.x` via tween. Tweening a Physics Arcade
    // Sprite's position directly fights Arcade Physics and was the
    // cause of the boss-fight freeze.
    const enterSpeed = (GAME_WIDTH * 0.42 - GAME_WIDTH) / 1.5; // px/sec
    (this.boss.body as Phaser.Physics.Arcade.Body).setVelocityX(enterSpeed);
    this.time.delayedCall(1500, () => {
      this.boss?.markEntered();
      if (this.boss?.body) (this.boss.body as Phaser.Physics.Arcade.Body).setVelocityX(0);
    });
    this.boss.on('spawnMinion', () => {
      this.spawnBossMinion();
    });
    // Spawn pixel debris when boss sheds parts at HP thresholds
    this.boss.on('shedPart', (sx: number, sy: number) => {
      this.spawnBossDebris(sx, sy);
    });
    // Switch to boss music
    this.playBgm("bgm");
    let bName = phase.bossName;
    if (phase.id >= 13) {
      bName = `Waste Monster ${this.romanize(this.waves.current().wave - 13)}`;
    }
    this.flashLevel(bName);
  }

  private romanize(num: number): string {
    if (isNaN(num)) return NaN.toString();
    const digits = String(+num).split("");
    const key = ["","C","CC","CCC","CD","D","DC","DCC","DCCC","CM",
               "","X","XX","XXX","XL","L","LX","LXX","LXXX","XC",
               "","I","II","III","IV","V","VI","VII","VIII","IX"];
    let roman = "", i = 3;
    while (i--) roman = (key[+digits.pop()! + (i * 10)] || "") + roman;
    return Array(+digits.join("") + 1).join("M") + roman;
  }

  private spawnBossMinion(): void {
    if (!this.boss || !this.boss.active) return;
    const wave = this.waves.current().wave;
    const phase = this.waves.currentPhase();

    const getMinionKind = (): EnemyKind => {
      if (phase.id >= 13) {
        const kindsToMix: EnemyKind[] = ["cardboard", "can", "plastic", "biowaste", "toxic", "radioactive", "chemicals"];
        return kindsToMix[Math.floor(Math.random() * kindsToMix.length)];
      }
      return phase.enemyKind;
    };

    if (wave >= 8) {
      // Wave 8+: Orbiting Bodyguard Minions around the boss!
      const targetOrbitCount = Math.min(6, 1 + (wave - 8));
      
      let activeOrbitingCount = 0;
      this.enemies.children.iterate((e) => {
        const en = e as Enemy;
        if (en.active && en.isOrbiting) activeOrbitingCount++;
        return true;
      });

      if (activeOrbitingCount >= targetOrbitCount) return;

      const toSpawn = targetOrbitCount - activeOrbitingCount;
      const angleStep = (Math.PI * 2) / targetOrbitCount;

      for (let i = 0; i < toSpawn; i++) {
        const angle = (activeOrbitingCount + i) * angleStep;
        const radius = 110;
        const ox = this.boss.x + Math.cos(angle) * radius;
        const oy = this.boss.y + Math.sin(angle) * radius;

        const enemy = this.enemies.get(ox, oy) as Enemy | null;
        if (!enemy) continue;

        enemy.enableBody(true, ox, oy, true, true);
        enemy.setKind(getMinionKind());
        enemy.setActive(true);
        enemy.setVisible(true);
        enemy.hp = this.waves.enemyHp();
        enemy.maxHp = enemy.hp;
        enemy.setShooter(true);
        enemy.setOrbiting(true, angle, radius);
        enemy.setDepth(10);
      }
      return;
    }

    // Waves 1-7: Multishot / Spread minion directional bursts
    let minionCount = 1;
    if (wave >= 4) minionCount = 2;
    if (wave >= 6) minionCount = 3;

    const speed = 160 * this.waves.difficulty();
    for (let i = 0; i < minionCount; i++) {
      const enemy = this.enemies.get(this.boss.x - 20, this.boss.y) as Enemy | null;
      if (!enemy) continue;

      enemy.enableBody(true, this.boss.x - 20, this.boss.y, true, true);
      enemy.setKind(getMinionKind());
      enemy.setActive(true);
      enemy.setVisible(true);
      enemy.hp = this.waves.enemyHp();
      enemy.maxHp = enemy.hp;

      if (minionCount === 1) {
        enemy.setVelocity(-speed, 0);
      } else {
        const angleOffset = (i - (minionCount - 1) / 2) * 0.35;
        const vx = -Math.cos(angleOffset) * speed;
        const vy = Math.sin(angleOffset) * speed;
        enemy.setVelocity(vx, vy);
      }

      enemy.setDepth(10);
    }
  }

  private updateChompCooldownUI(now: number): void {
    if (!this.chompBtnBase || !this.chompProgressGfx || !this.player) return;
    const progress = this.player.getChompCooldownProgress(now);
    if (progress >= 1.0) {
      this.chompBtnBase.setAlpha(1.0);
      this.chompBtnText.setAlpha(1.0);
      this.chompProgressGfx.clear();
    } else {
      this.chompBtnBase.setAlpha(0.3);
      this.chompBtnText.setAlpha(0.3);
      this.chompProgressGfx.clear();
      this.chompProgressGfx.fillStyle(0xffffff, 0.3);
      this.chompProgressGfx.beginPath();
      this.chompProgressGfx.moveTo(this.chompBtnX, this.chompBtnY);
      this.chompProgressGfx.arc(this.chompBtnX, this.chompBtnY, this.chompBtnRadius, -Math.PI/2, -Math.PI/2 + (Math.PI * 2 * progress), false);
      this.chompProgressGfx.closePath();
      this.chompProgressGfx.fillPath();
    }
  }

  private updateChargeButtonUI(now: number): void {
    if (!this.chargeBtnText || !this.chargeBtnBase) return;

    if (this.chompKills === 0) {
      // 0 chomps: Greyed out
      this.chargeBtnText.setText("CHARGE\nLASER");
      this.chargeBtnText.setColor("#888888");
      this.chargeBtnBase.setAlpha(0.3);
      this.chargeBtnText.setAlpha(0.5);
      this.chargeBtnText.setAlpha(0.5);
      this.chargeBtnText.setScale(1);
    } else if (this.chargeFull) {
      const remainingSec = Math.ceil((this.chargeTimeoutAt - now) / 1000);
      this.chargeBtnText.setText(`FIRE\n${Math.max(0, remainingSec)}`);
      this.chargeBtnBase.setAlpha(1.0);
      this.chargeBtnText.setAlpha(1.0);
      if (Math.sin(now / 50) > 0) {
        this.chargeBtnText.setColor("#ffffff");
        this.chargeBtnText.setScale(1.2);
      } else {
        this.chargeBtnText.setColor("#aaaaaa");
        this.chargeBtnText.setScale(1);
      }
    } else if (this.chompKills >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
      // 10+ chomps, but not fully charged (Godzilla mode ready): flicker moderately
      this.chargeBtnText.setText("CHARGE\nLASER");
      this.chargeBtnBase.setAlpha(1.0);
      this.chargeBtnText.setAlpha(1.0);
      if (Math.sin(now / 150) > 0) {
        this.chargeBtnText.setColor("#ffffff");
        this.chargeBtnText.setScale(1.1);
      } else {
        this.chargeBtnText.setColor("#bbbbbb");
        this.chargeBtnText.setScale(1);
      }
    } else {
      // 1+ chomps, not godzilla, not fully charged
      this.chargeBtnText.setText("CHARGE\nLASER");
      this.chargeBtnBase.setAlpha(1.0);
      this.chargeBtnText.setAlpha(1.0);
      this.chargeBtnText.setColor("#ffffff");
      this.chargeBtnText.setScale(1);
    }
  }

  private bossShoot(now: number): void {
    if (!this.boss || !this.boss.canShoot(now)) return;
    this.boss.markShot(now);
    const { vx, vy } = this.boss.aimAt(this.player.x, this.player.y);
    const ability = this.waves.currentPhase().bossAbility;

    const fireNode = (angleOffset: number) => {
      const b = this.enemyBullets.get(this.boss!.x - 60, this.boss!.y) as Projectile | null;
      if (!b) return;
      b.fireBullet(this.boss!.x - 60, this.boss!.y);
      b.setTexture(TEX.BOSS_BULLET);
      (b.body as Phaser.Physics.Arcade.Body).setCircle(8, 4, 4);

      let scale = 1;
      if (ability === "PIERCING") scale = 2;
      b.setScale(scale);

      if (ability === "SHOT_POWER") {
        b.damage = 2;
      } else {
        b.damage = 1; // Default
      }

      // Apply angle offset if needed
      if (angleOffset === 0) {
        b.setVelocity(vx, vy);
      } else {
        // Rotate velocity vector by angleOffset radians
        const cos = Math.cos(angleOffset);
        const sin = Math.sin(angleOffset);
        const nx = vx * cos - vy * sin;
        const ny = vx * sin + vy * cos;
        b.setVelocity(nx, ny);
      }
    };

    if (ability === "MULTISHOT") {
      // Fire 3 shots rapidly
      fireNode(0);
      this.time.delayedCall(150, () => { if (this.boss) fireNode(0); });
      this.time.delayedCall(300, () => { if (this.boss) fireNode(0); });
    } else if (ability === "SPREAD") {
      // Fire 3 shots in a cone
      fireNode(0);
      fireNode(-0.25);
      fireNode(0.25);
    } else {
      fireNode(0);
    }

    this.sfx("enemy_shoot", { volume: 0.4 });
  }

  /** Called from killBoss & enemy kill counter - drops an upgrade pickup. */
  private dropBossUpgrade(x: number, y: number, isBoss = false): void {
    let id: UpgradeId | "LIFE" | null = null;
    if (this.recoveryDropQueue.length > 0) {
      id = this.recoveryDropQueue.shift()!; // Recovery drop priority queue pop
    } else {
      id = this.upgrades.nextDropUpgradeId(this.waves.current().wave, isBoss);
      if (!id) {
        id = "LIFE";
      }
    }
    const pickup = this.pickups.get(x, y) as UpgradePickup | null;
    if (!pickup) return;
    pickup.configure(id, this.time.now);
  }

  // ---- firing -----------------------------------------------------------

  private fireBulletBurst(now: number): void {
    if (!this.player) return;
    this.lastFireAt = now;
    const originX = this.player.x + 24;
    const originY = this.player.y;
    const straightCount = this.upgrades.state.multishot; // 1..5
    const spreadCount = this.upgrades.state.spread;      // 0..3
    const speed = 760;

    const fireNode = (yOffset: number, angle: number) => {
      const b = this.bullets.get(originX, originY + yOffset) as Projectile | null;
      if (!b) return;
      b.setTexture(TEX.BULLET);
      b.fireBullet(originX, originY + yOffset);
      b.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);
      b.hitsRemaining = this.upgrades.piercingHits();
      b.damage = this.upgrades.shotDamage();
    };

    // Straight shots (Multishot adds straight lines, offset vertically)
    for (let i = 0; i < straightCount; i++) {
      const offset = straightCount > 1 ? (i - (straightCount - 1) / 2) * 14 : 0;
      fireNode(offset, 0);
    }

    // Spread shots (Spread adds 2 angled lines per level)
    for (let i = 1; i <= spreadCount; i++) {
      const angle = i * ACTIVE_DIFFICULTY.upgrades.SPREAD.anglePerLevel; // Spread angle
      fireNode(0, angle);
      fireNode(0, -angle);
    }
    
    this.sfx("shoot", { volume: 0.75 });
  }

  private fireChomp(now: number): void {
    if (!this.player) return;
    const origin = this.player.chompOrigin();
    const c = this.chomps.get(origin.x, origin.y) as Projectile | null;
    if (!c) return;
    c.fireChomp(origin.x, origin.y, origin.facing);
    c.setData("bornAt", now);
    c.setActive(true);
    c.setVisible(true);
    this.sfx("chomp", { volume: 0.5 });
    this.player.markChompUsed(now);
    // Damage any enemy within range immediately.
    let chomped = 0;
    this.enemies.children.iterate((e) => {
      const en = e as Enemy;
      if (!en.active) return true;
      const dx = en.x - origin.x;
      const dy = en.y - origin.y;
      if (Math.hypot(dx, dy) <= this.player.chompRangePx()) {
        const gain = en.isShooter ? 2 : 1;
        if (en.isShooter) {
          this.flashPickupBanner("+2 CHOMP CHARGES!");
        }
        this.killEnemy(en);
        chomped += gain;
      }
      return true;
    });

    // Damage boss if within chomp range
    if (this.boss && this.boss.active) {
      const dx = this.boss.x - origin.x;
      const dy = this.boss.y - origin.y;
      const bossWidth = this.boss.displayWidth || 210;
      const bossHeight = this.boss.displayHeight || 230;
      const bossRadius = Math.max(bossWidth, bossHeight) * 0.6;
      if (Math.hypot(dx, dy) <= this.player.chompRangePx() + bossRadius) {
        // Full normal shot volley damage (shot power multiplier * multishot count)
        const damage = this.upgrades.shotDamage() * Math.max(1, this.upgrades.state.multishot);
        if (this.boss.damage(damage)) {
          this.killBoss();
        }
        chomped++;
      }
    }

    if (chomped > 0) this.registerChomps(chomped);
  }

  /**
   * Player has chomped `n` enemies. Increment the counter and trigger the
   * godzilla transform when we cross the threshold. Heal one life per
   * chomp (per user spec 2026-08-04).
   */
  private registerChomps(n: number): void {
    this.chompKills += n;
    // Godzilla mode triggers on active charge now, not passively on threshold.
    this.refreshHud();
  }

  /**
   * Switch the player sprite to the godzilla placeholder and flag the
   * beam to fire forward instead of vertically. The placeholder is a
   * white rectangle; replacing `boss_godzilla.png` will swap it
   * automatically.
   */
  private activateGodzilla(): void {
    if (this.chompKills >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
      this.godzillaText.setVisible(true);
      this.godzillaText.setText("GODZILLA MODE — BEAM UNLEASHED");
    }
    if (this.player) {
      this.player.setTexture(TEX.PLAYER_GODZILLA);
      this.player.setOrigin(0.5, 0.5);
      this.player.setScale(0.6);
      // Re-apply the smaller hitbox (same formula as Player constructor)
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      const bw = this.player.width;
      const bh = this.player.height;
      body.setSize(bw * 0.65, bh * 0.80);
      body.setOffset(bw * 0.25, bh * 0.10);
    }
  }

  private deactivateGodzilla(): void {
    this.godzillaText.setVisible(false);
    if (this.player) {
      this.player.setTexture(TEX.PLAYER);
      this.player.setOrigin(0.5, 0.5);
      this.player.setScale(Math.min(72 / 300, 43 / 190)); // default scale based on texW=300, texH=190
      // Re-apply the smaller hitbox (same formula as Player constructor)
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      const bw = this.player.width;
      const bh = this.player.height;
      body.setSize(bw * 0.65, bh * 0.80);
      body.setOffset(bw * 0.25, bh * 0.10);
    }
  }

  private fireBeam(now: number): void {
    if (!this.player) return;
    
    this.player.charging = false;
    this.player.chargeLevel = 0;
    this.chargeBar.setVisible(false);
    
    const power = Math.max(1, this.chompKills);
    this.godzillaFiring = true;
    
    if (power >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
      this.sfx("laser_long", { volume: 0.7 });
      this.playBgm("bgm");
    } else {
      this.sfx("laser_short", { volume: 0.7 });
    }
    
    // Duration scales with power (max 5000ms at 10 charges)
    const duration = power >= ACTIVE_DIFFICULTY.chompsForGodzilla ? 5000 : (power * 500);
    this.beamEndTime = now + duration;
    this.beamDamageTickAt = 0; // Trigger damage immediately on first update tick
  }

  private updateContinuousBeam(now: number): void {
    if (!this.player) return;
    
    const power = Math.max(1, this.chompKills);
    const beamThickness = 8 + power * 3;
    let beamStart = this.player.x + 30;
    let beamY = this.player.y;
    
    if (power >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
      // Godzilla beam originates at the right edge of the sprite (opened mouth)
      beamStart = this.player.x + (this.player.displayWidth * 0.48);
      beamY = this.player.y - this.player.displayHeight * 0.38 + beamThickness / 2;
    }
    
    const beamRange = GAME_WIDTH * 1.5; // Always reach left end of screen
    
    // Screen shake while firing
    this.cameras.main.shake(100, 0.003);

    // Visual effect: solid core line + scattered static lines
    this.beamGraphics.clear();
    this.beamGraphics.setAlpha(1);
    this.beamGraphics.lineStyle(beamThickness, 0xffffff, 1);
    this.beamGraphics.beginPath();
    this.beamGraphics.moveTo(beamStart, beamY);
    this.beamGraphics.lineTo(beamStart + beamRange, beamY);
    this.beamGraphics.strokePath();
    
    // extra erratic lines
    this.beamGraphics.lineStyle(2, 0xffffff, 0.8);
    this.beamGraphics.beginPath();
    for (let i = 0; i < 4; i++) {
       const yOff = (Math.random() - 0.5) * (beamThickness + 20);
       this.beamGraphics.moveTo(beamStart, beamY + yOff);
       this.beamGraphics.lineTo(beamStart + beamRange + (Math.random() * 50), beamY + yOff);
    }
    this.beamGraphics.strokePath();

    // Damage tick every 200ms
    if (now >= this.beamDamageTickAt) {
      this.beamDamageTickAt = now + 200;
      
      const damage = Math.floor(2 + power * 0.5);
      const bossDamage = Math.floor(4 + power * 0.25);
      const bossThickness = beamThickness + 40;
      
      this.enemies.children.iterate((e) => {
        const en = e as Enemy;
        if (!en.active) return true;
        if (
          en.x >= beamStart &&
          en.x <= beamStart + beamRange &&
          Math.abs(en.y - beamY) < beamThickness
        ) {
          if (en.damage(damage)) this.killEnemy(en);
        }
        return true;
      });
      if (this.boss && this.boss.entered()) {
        if (this.boss.x >= beamStart && Math.abs(this.boss.y - beamY) < bossThickness) {
          if (this.boss.damage(bossDamage)) this.killBoss();
        }
      }
    }
  }

  private endContinuousBeam(): void {
    this.godzillaFiring = false;
    this.beamGraphics.clear();
    
    if (this.chompKills >= ACTIVE_DIFFICULTY.chompsForGodzilla) {
      this.deactivateGodzilla();
      // Resume normal or boss BGM
      this.playBgm("bgm");
    }
    
    this.chompKills = 0;
    this.refreshHud();
  }

  private drawChargeBar(lvl: number): void {
    this.chargeBar.clear();
    const w = 80;
    const h = 6;
    const x = this.player.x - w / 2;
    const y = this.player.y - 36;
    this.chargeBar.lineStyle(1, 0xffffff, 0.6);
    this.chargeBar.strokeRect(x, y, w, h);
    this.chargeBar.fillStyle(0xffffff, 1);
    this.chargeBar.fillRect(x, y, w * lvl, h);
  }

  // ---- collisions --------------------------------------------------------

  private onBulletHitsEnemy(bullet: Projectile, enemy: Enemy): void {
    if (!bullet.active || !enemy.active) return;
    if (bullet.hitEnemies && bullet.hitEnemies.has(enemy)) return; // Already hit this enemy
    
    if (enemy.damage(bullet.damage)) {
      this.killEnemy(enemy);
    }
    
    if (bullet.kind === "bullet") {
      bullet.hitEnemies.add(enemy);
      bullet.hitsRemaining -= 1;
      if (bullet.hitsRemaining <= 0) bullet.reset();
    }
  }

  private onBulletHitsBoss(bullet: Projectile, boss: Boss): void {
    if (!bullet.active || !boss.entered()) return;
    if (bullet.hitEnemies && bullet.hitEnemies.has(boss)) return;

    if (boss.damage(bullet.damage)) this.killBoss();
    
    if (bullet.kind === "bullet") {
      bullet.hitEnemies.add(boss);
      bullet.hitsRemaining -= 1;
      if (bullet.hitsRemaining <= 0) bullet.reset();
    }
  }

  /** Per-player damage-invulnerability flag. Phaser's overlap callback
   *  fires every frame while two bodies intersect, so we have to debounce
   *  ourselves to avoid instantly draining 3 lives on a single touch. */
  private playerHitUntil = 0;
  private readonly playerHitCooldownMs = 800;

  private isPlayerInvulnerable(now: number): boolean {
    return now < this.playerHitUntil;
  }
  private markPlayerHit(now: number): void {
    this.playerHitUntil = now + this.playerHitCooldownMs;
  }

  private handlePlayerHitPenalty(lifeLost: boolean): void {
    if (ACTIVE_DIFFICULTY.upgradeLossCondition === "none") return;
    if (ACTIVE_DIFFICULTY.upgradeLossCondition === "life" && !lifeLost) return;
    
    this.lostHpCounter += 1;
    if (ACTIVE_DIFFICULTY.hitsBeforeUpgradeLoss > 0 && this.lostHpCounter % ACTIVE_DIFFICULTY.hitsBeforeUpgradeLoss === 0) {
      for (let i = 0; i < ACTIVE_DIFFICULTY.upgradesLostPerPenalty; i++) {
        const degraded = this.upgrades.degradeRandom(this.waves.bossesDefeated);
        if (degraded) {
          this.recoveryDropQueue.push(degraded.id);
          this.flashPickupBanner(`PENALTY: LOST ${UPGRADE_LABELS[degraded.id]}!`);
        }
      }
    }
  }

  private onPlayerHitsEnemy(): void {
    if (this.gameOver) return;
    const now = this.time.now;
    if (this.isPlayerInvulnerable(now)) return; // Ignore the same touch event while invulnerable.
    const hitResult = this.player.takeHit(now);
    this.markPlayerHit(now);
    this.handlePlayerHitPenalty(hitResult.lifeLost);
    this.refreshHud();
    this.spawnExplosion(this.player.x, this.player.y);
    if (hitResult.gameover) this.endGame();
  }

  private onPlayerHitsBullet(): void {
    if (this.gameOver) return;
    const now = this.time.now;
    if (this.isPlayerInvulnerable(now)) return;
    const hitResult = this.player.takeHit(now);
    this.markPlayerHit(now);
    this.handlePlayerHitPenalty(hitResult.lifeLost);
    this.refreshHud();
    if (hitResult.gameover) this.endGame();
  }

  private waveScoreMultiplier(): number {
    return 1 + (this.waves.current().wave - 1) * ACTIVE_DIFFICULTY.scoreMultiplierPerWave;
  }

  private onPlayerPicksUpUpgrade(pickup: UpgradePickup): void {
    if (!pickup.active || !pickup.upgradeId) return;
    pickup.collect();
    this.sfx("powerup", { volume: 0.6 });
    this.score += Math.floor(50 * this.waveScoreMultiplier());
    this.refreshHud();

    if (pickup.upgradeId === "LIFE") {
      this.handleHpOrFallbackPickup();
    } else {
      const newLevel = this.upgrades.apply(pickup.upgradeId as UpgradeId);
      this.flashPickupBanner(`${UPGRADE_LABELS[pickup.upgradeId as UpgradeId]} → L${newLevel}`);
      this.drawLivesIcons();
    }
  }

  private handleHpOrFallbackPickup(): void {
    if (!this.player) return;
    if (this.player.hp < this.player.maxHp) {
      this.player.hp = this.player.maxHp;
      this.player.invincibleUntil = 0;
      this.player.setVisible(true);
      this.flashPickupBanner("HP RESTORED!");
      this.refreshHud();
    } else if (this.chompKills < ACTIVE_DIFFICULTY.chompsForGodzilla) {
      this.chompKills = Math.min(ACTIVE_DIFFICULTY.chompsForGodzilla, this.chompKills + 5);
      this.flashPickupBanner("+5 CHOMP CHARGES!");
      this.refreshHud();
    } else {
      const bonusPoints = 10000;
      this.score += bonusPoints;
      this.flashPickupBanner("+10,000 BONUS POINTS!");
      this.refreshHud();
    }
  }

  private killEnemy(enemy: Enemy): void {
    this.score += Math.floor(enemy.scoreValue * this.waveScoreMultiplier());
    this.spawnExplosion(enemy.x, enemy.y);
    enemy.disableBody(true, true);
    this.killsSinceLastUpgrade += 1;
    
    // Drop one upgrade every N enemies
    if (this.killsSinceLastUpgrade >= ACTIVE_DIFFICULTY.killsPerUpgradeDrop) {
      this.dropBossUpgrade(enemy.x, enemy.y, false);
      this.killsSinceLastUpgrade = 0;
    }
    
    // Increase Max HP every N enemies (if configured)
    if (ACTIVE_DIFFICULTY.maxHpIncreaseCondition === "kills" && this.player && this.player.maxHp < ACTIVE_DIFFICULTY.maxHpCap) {
      this.killsSinceLastHpIncrease += 1;
      if (this.killsSinceLastHpIncrease >= ACTIVE_DIFFICULTY.killsPerMaxHpIncrease) {
        this.player.maxHp = Math.min(ACTIVE_DIFFICULTY.maxHpCap, this.player.maxHp + ACTIVE_DIFFICULTY.maxHpIncreaseAmount);
        this.player.hp = this.player.maxHp; // Heal to full when upgrading HP
        this.flashPickupBanner("MAX SHIELD INCREASED!");
        this.killsSinceLastHpIncrease = 0;
      }
    }
    
    this.refreshHud();
  }

  /**
   * Boss kill — per user spec, grant +1 life on each boss defeat.
   */
  private killBoss(): void {
    if (!this.boss) return;
    this.score += Math.floor(this.boss.scoreValue * this.waveScoreMultiplier());
    this.refreshHud();
    // Pixel shatter death effect + screen shake
    this.boss.deathShatter();
    this.cameras.main.shake(300, 0.008);
    
    // Save position before cleaning up boss reference
    const bossX = this.boss.x;
    const bossY = this.boss.y;
    
    this.boss.destroyEyes();
    this.boss.disableBody(true, true);
    this.boss = null;
    this.waves.bossesDefeated += 1;
    // do not reset killsSinceLastUpgrade here so it tracks globally across waves
    // Resume normal BGM
    this.playBgm("bgm");
    // Grant maxHp based on config and fully heal.
    if (this.player) {
      if (ACTIVE_DIFFICULTY.maxHpIncreaseCondition === "boss") {
        this.player.maxHp = Math.min(ACTIVE_DIFFICULTY.maxHpCap, this.player.maxHp + ACTIVE_DIFFICULTY.maxHpIncreaseAmount);
      }
      this.player.hp = this.player.maxHp;
    }
    
    if (ACTIVE_DIFFICULTY.bossDropsUpgrade) {
      for (let i = 0; i < ACTIVE_DIFFICULTY.bossUpgradeDropCount; i++) {
        const ox = bossX + (Math.random() - 0.5) * 40;
        const oy = bossY + (Math.random() - 0.5) * 40;
        this.dropBossUpgrade(ox, oy, true);
      }
    }

    // Victory check — if player defeated Phase 15 ("FINAL WASTEMONSTER III"), trigger victory!
    if (this.waves.currentPhase().id === 15) {
      const VICTORY_BONUS = ACTIVE_DIFFICULTY.victoryBonus;
      this.score += VICTORY_BONUS;
      this.refreshHud();
      void submitRunScore(this.score, 15, true);
      showCongratsPanel(document.getElementById("overlay-root"), this.score, 15);
      this.scene.pause();
      return;
    }

    this.shootingEnemiesSpawnedThisWave = 0;
    const next = this.waves.advance();
    void next;
    this.flashLevel(next.phase.name);
    this.refreshHud();
  }

  // ---- effects + ui ------------------------------------------------------

  /**
   * Brief flash banner announcing a level transition.
   */
  private flashLevel(name: string): void {
    const t = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80, name, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: "28px",
        color: "#ffffff",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(100);
    this.tweens.add({
      targets: t,
      alpha: 0,
      duration: 1500,
      ease: "Cubic.easeIn",
      onComplete: () => t.destroy(),
    });
  }

  private flashPickupBanner(text: string): void {
    if (this.pickupBannerText) this.pickupBannerText.destroy();
    const t = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, text, this.hudStyle(20))
      .setOrigin(0.5)
      .setDepth(100);
    this.pickupBannerText = t;
    this.tweens.add({
      targets: t,
      alpha: 0,
      y: t.y - 40,
      duration: 900,
      onComplete: () => {
        if (this.pickupBannerText === t) this.pickupBannerText = null;
        t.destroy();
      },
    });
  }

  private spawnExplosion(x: number, y: number): void {
    const e = this.add.sprite(x, y, TEX.EXPLOSION);
    e.setOrigin(0.5, 0.5);
    this.tweens.add({
      targets: e,
      scale: 1.6,
      alpha: 0,
      duration: 220,
      onComplete: () => e.destroy(),
    });
  }

  private spawnBossDebris(x: number, y: number): void {
    const count = 10;
    for (let i = 0; i < count; i++) {
      const size = Phaser.Math.Between(4, 10);
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(-size / 2, -size / 2, size, size);
      g.setPosition(
        x + Phaser.Math.Between(-30, 30),
        y + Phaser.Math.Between(-30, 30)
      );
      g.setDepth(20);
      const vx = Phaser.Math.Between(-150, 150);
      const vy = Phaser.Math.Between(-150, 150);
      this.tweens.add({
        targets: g,
        x: g.x + vx,
        y: g.y + vy,
        alpha: 0,
        duration: Phaser.Math.Between(500, 1000),
        ease: 'Cubic.easeOut',
        onComplete: () => g.destroy(),
      });
    }
  }

  // ---- game over / restart -----------------------------------------------

  private async endGame(): Promise<void> {
    if (this.gameOver) return;
    this.gameOver = true;
    this.chargeBar.setVisible(false);
    // Submit the score — best-effort. Don't await it inline because it
    // can hang if the DB is slow; if it does hang, the GameOverScene
    // starts anyway and the overlay's REST call eventually returns.
    void submitRunScore(this.score, this.waves.currentPhase().id);
    const overlayRoot = document.getElementById("overlay-root");
    showGameOver(
      overlayRoot,
      this.score,
      this.waves.currentPhase().id,
      this.waves.current().wave
    );
    this.scene.start("GameOverScene", { score: this.score });
  }

  public restart(): void {
    if (this.chargeSoundInstance) {
      this.chargeSoundInstance.stop();
    }
    if (!this.gameOver && this.score > 0) {
      void submitRunScore(this.score, this.waves.currentPhase().id);
    }
    this.upgrades.reset();
    clearPlayerIdent();
    const root = document.getElementById("overlay-root");
    void showInfoPrompt(root);
    this.scene.start("StartScene");
  }

  private shootingEnemiesSpawnedThisWave = 0;
  private pickupBannerText: Phaser.GameObjects.Text | null = null;

  private grantRandomStarterUpgrade(): void {
    const starterPool: UpgradeId[] = ["FIRE_RATE", "PIERCING", "SPEED", "SHOT_POWER"];
    const picked = starterPool[Math.floor(Math.random() * starterPool.length)];
    const newLvl = this.upgrades.apply(picked);
    this.flashPickupBanner(`STARTER POWER: ${UPGRADE_LABELS[picked]} L${newLvl}`);
    this.drawLivesIcons();
    this.refreshHud();
  }
}
